import React, { useCallback, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "../Hooks";
import {
  getThemes,
  copyTheme,
  deleteTheme,
} from "../../requests/themeManagementRequests";
import { PageWrapper } from "../UI/Grid";
import Table from "../UI/Table";
import AlertContext from "../../context/alert/AlertContext";
import UserContext from "../../context/user/UserContext";

const ThemesPage = () => {
  const { showSuccessAlert, showWarningAlert, showFailureAlert } =
    useContext(AlertContext);
  const { pid } = useParams();
  const navigate = useNavigate();
  const { activeUser } = useContext(UserContext);

  const queryVariables = useMemo(() => {
    if (activeUser?.roleLevel === 9) return {};
    else return { pid };
  }, [activeUser, pid]);

  const result = useQuery({
    queryID: "themes",
    request: getThemes,
    queryVariables,

    skip: !pid,
  });

  const { data, isLoading, refetchQuery } = result || {};

  const { rows: themes = [] } = data || {};

  const onCardClick = (id) => {
    navigate(`/Theme/${id}`);
  };

  const getIcon = (pid) => {
    switch (pid) {
      case 9:
        return "faUserAstronaut";
      case 5:
        return "faUserTie";
      case 1:
        return "faUserCheck";
      default:
        return "faPeopleGroup";
    }
  };

  const onCopy = useCallback(
    async (id) => {
      showWarningAlert(`Copying Theme: ${id}`);
      try {
        await copyTheme({ id });
        await refetchQuery();
        showSuccessAlert(`Successfully copied Theme: ${id}`);
      } catch (error) {
        console.error(error);
        showFailureAlert(`Failed to copy Theme: ${id}`);
      }
    },
    [refetchQuery, showFailureAlert, showSuccessAlert, showWarningAlert]
  );

  const onDelete = useCallback(
    async (id) => {
      showWarningAlert(`Deleting Theme: ${id}`);
      try {
        await deleteTheme({ id });
        await refetchQuery();
        showSuccessAlert(`Successfully deleted Theme: ${id}`);
      } catch (error) {
        console.error(error);
        showFailureAlert(`Failed to delete Theme: ${id}`);
      }
    },
    [refetchQuery, showFailureAlert, showSuccessAlert, showWarningAlert]
  );

  const gridData = useMemo(() => {
    if (!result.data?.rows) return null;
    return themes.map(({ id, name, description, pid }) => ({
      id,
      title: name,
      subtitle: description,
      iconName: getIcon(pid),
      onCopy: () => onCopy(id),
      onDelete: () => onDelete(id),
    }));
  }, [result, onCopy, onDelete, themes]);

  return (
    <>
      <PageWrapper>
        <Table
          createLink={() => navigate("/Theme/create")}
          createButtonText="Create Theme"
          createButtonIcon="faIdBadge"
          title="Theme Management"
          backButtonLink="/Administration"
          gridData={gridData}
          hideProperties
          hidePager
          hideSearch
          isLoading={isLoading}
          hideToggle
          forceGrid
          onCardClick={(theme) => onCardClick(theme.id)}
        />
      </PageWrapper>
    </>
  );
};

export default ThemesPage;
